import { ExternalLink, Sparkles, Terminal, ThumbsDown, ThumbsUp } from 'lucide-react'
import ModuleLayout from '../components/ModuleLayout'
import StepByStep from '../components/StepByStep'
import CopyBlock from '../components/CopyBlock'
import SpreadsheetGrid from '../components/SpreadsheetGrid'
import SheetSubmission from '../components/SheetSubmission'
import {
  AP_HEADERS, AP_TAB_NAME, AMOUNT_BANDS, BAND_COLUMN_HEADER, ROW_COUNT,
  SAMPLE_ROWS, STATUSES, SUMMARY_HEADERS, SUMMARY_TAB_NAME, bandFor, money,
} from '../data/apDataset'
import { MODULE_20_CHECKS } from '../data/apChecks'

/**
 * Module 4 of the AI track (id 20).
 *
 * No <Quiz> here, for the same reason as module 19: Quiz and SheetSubmission
 * both write quizResults.percentageScore and both call completeModule, so a
 * module with both has them overwrite each other depending on finish order.
 *
 * Every prompt below is generated from apDataset, so the wording the trainee
 * copies and the values the checker expects cannot drift apart. If you reword
 * a prompt by hand, check src/data/apChecks.js still asks for the same thing.
 */

const BAND_RULES = AMOUNT_BANDS.map((b) => `  ${b.label} — ${b.describes}`).join('\n')

const SORT_PROMPT = `In my sheet, on the "${AP_TAB_NAME}" tab, please sort the invoices by the
Amount column, largest at the top. Keep the headings in row 1 where they
are, and don't change any of the values — just reorder the rows.`

const BAND_PROMPT = `On the "${AP_TAB_NAME}" tab, add one new column in column G, headed
"${BAND_COLUMN_HEADER}". Fill it in for every invoice using exactly these
three labels, spelled exactly like this:

${BAND_RULES}

Type the labels as plain text — I don't want a formula in that column.`

const SUMMARY_PROMPT = `Add a new tab to the same spreadsheet called "${SUMMARY_TAB_NAME}".

Put "${SUMMARY_HEADERS[0]}" in A1 and "${SUMMARY_HEADERS[1]}" in B1, and nothing else in row 1.
Below that, one row per status that appears on the "${AP_TAB_NAME}" tab
(${STATUSES.join(', ')}), with the total Amount for that status beside it
as a number. Don't change the "${AP_TAB_NAME}" tab while you're at it.`

const VAGUE_PROMPT = `Can you tidy up my invoice sheet and add a summary?`

function SectionWhatYoullDo() {
  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">What you're about to do</h2>
      <p className="mb-4 text-slate-600">
        Same spreadsheet as last time. You're going to make three changes to it — sort it, add a
        column, and build a second tab — and you're going to do all three by asking, without
        touching a cell yourself.
      </p>
      <p className="mb-4 text-slate-600">
        Last module the point was <em>that</em> it works. This one is about <strong>how you
        ask</strong>. The same request, worded two ways, gives you two very different afternoons.
      </p>
      <div className="rounded-xl border-2 border-teal-200 bg-teal-50 p-5">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-teal-600" />
          <div>
            <h3 className="font-semibold text-teal-900">Keep using the sheet you already made</h3>
            <p className="mt-1 text-sm text-teal-800">
              Everything below assumes the {AP_TAB_NAME} tab from{' '}
              <strong>Ask for the Data</strong>, with all {ROW_COUNT} invoices in it. If you've lost
              it, go back and run that script again — it takes a minute, which is exactly why you
              asked for a script instead of a table.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

function SectionVagueVsSpecific() {
  const missing = [
    'Which column to sort on, and which way up',
    'Whether the headings should move with the rows',
    'What the new column should be called, and what goes in it',
    'What "a summary" means — of what, grouped how, on which tab',
    'Whether it may change the numbers, or only rearrange them',
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">Specific in, great out</h2>
      <p className="mb-5 text-slate-600">
        Here is the request most people type first. It isn't wrong, exactly — you'll get{' '}
        <em>something</em> back. You just have no idea what.
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border-2 border-rose-200 bg-rose-50 p-4">
          <div className="mb-2 flex items-center gap-2 font-semibold text-rose-900">
            <ThumbsDown className="h-4 w-4" /> Vague
          </div>
          <p className="rounded-lg border border-rose-200 bg-white p-3 font-mono text-xs text-slate-700">
            {VAGUE_PROMPT}
          </p>
          <p className="mt-3 text-sm text-rose-800">
            You'll get a tidy sheet. Possibly sorted by date. Possibly with the columns renamed,
            the amounts reformatted, and a summary of something you didn't ask about — on a tab
            called whatever it felt like calling it.
          </p>
        </div>

        <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4">
          <div className="mb-2 flex items-center gap-2 font-semibold text-emerald-900">
            <ThumbsUp className="h-4 w-4" /> Specific
          </div>
          <p className="rounded-lg border border-emerald-200 bg-white p-3 font-mono text-xs text-slate-700">
            {SORT_PROMPT}
          </p>
          <p className="mt-3 text-sm text-emerald-800">
            Same job, but there's nothing left to guess: which tab, which column, which direction,
            what must stay put, and what it isn't allowed to touch.
          </p>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-semibold text-slate-800">What the vague version left it to decide</h3>
        <ul className="mt-2 space-y-1.5 text-sm text-slate-600">
          {missing.map((m) => (
            <li key={m} className="flex gap-2">
              <span className="text-slate-400">•</span>
              {m}
            </li>
          ))}
        </ul>
        <p className="mt-4 border-t border-slate-100 pt-4 text-sm text-slate-700">
          <strong className="text-slate-800">This is the whole idea:</strong> anything you leave
          out, it decides for you — sensibly, confidently, and possibly differently next time. In a
          finance context that's the difference between a report you can hand over and one you have
          to re-check by hand.
        </p>
      </div>
    </section>
  )
}

function SectionTheThreeAsks() {
  const steps = [
    {
      title: 'Sort the invoices, biggest first',
      content: (
        <div className="space-y-3">
          <p>
            Open your terminal, type{' '}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">wa</code>, and paste your
            sheet link so it knows which spreadsheet you mean. Then ask for this.
          </p>
          <CopyBlock text={SORT_PROMPT} label="Copy prompt" />
        </div>
      ),
      highlight: 'It will ask your permission before it writes. Read what it says it\'s about to do — that prompt is the control point, not a formality.',
    },
    {
      title: `Add the "${BAND_COLUMN_HEADER}" column`,
      content: (
        <div className="space-y-3">
          <p>
            Now a column that doesn't exist in the data — you're asking it to make a judgement on
            every row. Notice how much of this prompt is spent on the exact labels and the exact
            cut-offs.
          </p>
          <CopyBlock text={BAND_PROMPT} label="Copy prompt" />
          <p className="text-sm text-slate-500">
            How many of each you end up with depends on the register you generated — nobody else's
            split will match yours. What has to be true is that every row's band matches its own
            amount, and that is what gets checked.
          </p>
        </div>
      ),
      highlight: 'Say "exactly these labels" and mean it. "High/Mid/Low" is a perfectly reasonable answer to a question you didn\'t ask precisely.',
    },
    {
      title: `Build the ${SUMMARY_TAB_NAME} tab`,
      content: (
        <div className="space-y-3">
          <p>
            Last one, and the closest to real work: totals by status, on their own tab, leaving the
            register alone.
          </p>
          <CopyBlock text={SUMMARY_PROMPT} label="Copy prompt" />
        </div>
      ),
      highlight: 'Telling it what NOT to touch is as useful as telling it what to do.',
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">Three asks, one at a time</h2>
      <p className="mb-5 text-slate-600">
        You could ask for all three in one go, and it would probably manage. Doing them one at a
        time means when something comes out wrong you know exactly which instruction was the
        unclear one.
      </p>
      <StepByStep steps={steps} />
    </section>
  )
}

function SectionTargetShape() {
  const topRows = [...SAMPLE_ROWS]
    .sort((a, b) => b[3] - a[3])
    .map((r) => [r[0], r[1], r[2], `$${money(r[3])}`, r[4], r[5], bandFor(r[3])])
  const invoiceGrid = [[...AP_HEADERS, BAND_COLUMN_HEADER], ...topRows]
  const summaryGrid = [
    SUMMARY_HEADERS,
    ...STATUSES.map((s) => [s, 'your total']),
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">What you're aiming at</h2>
      <p className="mb-4 text-slate-600">
        The top of your {AP_TAB_NAME} tab should end up shaped like this — your own invoices,
        reordered, with one new column on the end. The rows below are somebody else's; yours will
        have different vendors and amounts.
      </p>
      <SpreadsheetGrid data={invoiceGrid} compact />
      <p className="mt-2 text-sm text-slate-500">
        Illustration only — all {ROW_COUNT} of your invoices stay in the sheet.
      </p>

      <p className="mb-4 mt-6 text-slate-600">
        And the {SUMMARY_TAB_NAME} tab, in whatever order it happens to list the statuses:
      </p>
      <SpreadsheetGrid data={summaryGrid} compact />
      <p className="mt-2 text-sm text-slate-500">
        The check below doesn't care about the row order, and won't mind an extra grand-total line.
        It adds up your own register and compares — so the totals have to match the rows you
        actually have, not anybody else's.
      </p>
    </section>
  )
}

function SectionWhatJustHappened() {
  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">Worth noticing before you move on</h2>
      <div className="space-y-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-800">You described the outcome, not the steps</h3>
          <p className="mt-1 text-sm text-slate-600">
            Nowhere did you say "select A1:F13, then Data ▸ Sort range". You said what the sheet
            should look like afterwards. That's the shift — and it's why being precise about the
            outcome matters so much.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-800">The bands are now frozen text</h3>
          <p className="mt-1 text-sm text-slate-600">
            You asked for plain text, not a formula. So if someone edits an amount tomorrow, the
            band beside it quietly becomes a lie. Hold that thought — it's the entire subject of
            the next module.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-800">A specific ask is a checkable ask</h3>
          <p className="mt-1 text-sm text-slate-600">
            The only reason a page can mark this exercise for you is that the request was precise
            enough to have one right answer. The same is true of anything you hand to your manager:
            if nobody can say what "done properly" looks like, nobody can confirm it was.
          </p>
        </div>
      </div>
    </section>
  )
}

function SectionTroubleshooting() {
  const problems = [
    {
      p: 'The headings got sorted into the middle of the data',
      s: 'It treated row 1 as data. Tell it: "row 1 is the headings — put them back at the top and sort only the rows below." You don\'t need to start over.',
    },
    {
      p: 'The band labels came back as High / Medium / Low',
      s: 'A reasonable answer to a vague question. Ask again and quote the three labels exactly, in quotes. This is the lesson, arriving on schedule.',
    },
    {
      p: 'The Summary tab came back with an extra column, like a count',
      s: 'That\'s fine — the check reads Status in A1 and Total in B1 and ignores anything past column B. It only matters if the extra column has pushed Total out of B; if it has, ask for "Status in A1 and Total in B1".',
    },
    {
      p: 'The amounts are now text, or the totals show as 0',
      s: 'Ask: "the Amount column should be numbers, not text — please fix it and redo the Summary totals."',
    },
    {
      p: 'It has made a mess of the whole sheet',
      s: 'File ▸ Version history ▸ See version history, and restore the version from before. Then ask again, one change at a time. Knowing this exists is worth more than any prompt on this page.',
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">If it goes wrong</h2>
      <p className="mb-4 text-slate-600">
        None of these mean you've broken anything. Say what's wrong in plain English and it'll
        have another go.
      </p>
      <dl className="space-y-3">
        {problems.map(({ p, s }) => (
          <div key={p} className="rounded-lg border border-slate-200 bg-white p-4">
            <dt className="flex items-start gap-2 text-sm font-semibold text-slate-800">
              <Terminal className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              {p}
            </dt>
            <dd className="mt-1.5 pl-6 text-sm text-slate-600">{s}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-sm text-slate-500">
        Still stuck?{' '}
        <a
          href="https://sheets.new"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
        >
          Start a fresh sheet <ExternalLink className="h-3 w-3" />
        </a>{' '}
        and run the script from Ask for the Data again — a minute, and cleaner than untangling a
        broken sheet.
      </p>
    </section>
  )
}

export default function ModulePromptingForSheets() {
  return (
    <ModuleLayout moduleId={20}>
      <div className="space-y-12">
        <SectionWhatYoullDo />
        <SectionVagueVsSpecific />
        <SectionTheThreeAsks />
        <SectionTargetShape />
        <SectionWhatJustHappened />
        <SectionTroubleshooting />
        <SheetSubmission
          moduleId={20}
          checks={MODULE_20_CHECKS}
          sourceModuleId={19}
          heading="Check your sheet"
          intro="Same spreadsheet as last time — the link is filled in for you. Check as many times as you like."
        />
      </div>
    </ModuleLayout>
  )
}
